import { push } from 'connected-react-router'
import { endOfMonth, isSameDay, startOfMonth } from 'date-fns'
import React, { FC, useCallback, useEffect } from 'react'
import styles from 'styles/components/template.module.scss'
import { Description, MainHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { useDispatch, useSelector } from 'react-redux'
import { Calendar } from 'components/Calendar'
import { CautionFixedLabel } from 'components/Label'
import { LargeMenuCard } from 'components/Card'
import { State } from 'index'
import { getClosedDates, getCurrentDate, getCurrentMonth, getSpecialHolidays } from 'reducks/calendar/selectors'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { BusinessHourCard, BusinessHourHelp } from 'components/Card'
import { openModal } from 'reducks/modal/operations'
import { getHolidays } from 'reducks/store/selectors'
import { getRole, getStoreRef } from 'reducks/auth/selectors'
import { Role } from 'lib/role'
import { fetchOrdersInPeriod, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { dateToString } from 'lib/helper'
import { updateEditState } from 'reducks/confirm/operations'
import { fetchStore } from 'reducks/store/operations'
import { useToggleCalendarType } from 'hooks/calendar'
import { TopTabs } from 'components/Tab'
import {
  fetchPreparingStoreShippings,
  initStoreShippings,
  unsubscribeStoreShippings,
} from 'reducks/storeShipping/operations'
import { selectDefaultBreadTypes } from 'reducks/auth/operations'
import { getPointCard } from 'reducks/pointCard/selectors'

const TopTemplate: FC = () => {
  const dispatch = useDispatch()
  const currentDate = useSelector((state: State) => getCurrentDate(state))
  const currentMonth = useSelector((state: State) => getCurrentMonth(state))
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const pointCard = useSelector((state: State) => getPointCard(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const role = useSelector((state: State) => getRole(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const roleManager = new Role(role, storeRef)
  const { calendarType, toggleCalendar } = useToggleCalendarType('daily')

  const openBusinessHourHelp = useCallback(
    () => dispatch(openModal(<BusinessHourHelp />, 'sacri上の営業時間について')),
    [dispatch]
  )

  // storeをrefetch
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStore(storeRef))
    dispatch(updateEditState(false))
  }, [storeRef])

  // 月が変わったら休業日を取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(
      fetchClosedDates(startOfMonth(currentMonth), endOfMonth(currentMonth), holidays, specialHolidays, storeRef)
    )
  }, [currentMonth, holidays, storeRef, specialHolidays])

  // 月が変わったら臨時休業日・営業日を再取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(currentMonth)))
  }, [currentMonth, storeRef])

  // 表示月が変わったら注文を取得する
  useEffect(() => {
    if (!currentMonth || !storeRef) return
    dispatch(initOrders())
    dispatch(
      fetchOrdersInPeriod(
        startOfMonth(currentMonth),
        endOfMonth(currentMonth),
        'deliver',
        storeRef,
        ['succeeded', 'requires_capture'],
        ['daily', 'preorder']
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [currentMonth, storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(initStoreShippings())
    dispatch(fetchPreparingStoreShippings(storeRef))
    return () => {
      dispatch(unsubscribeStoreShippings())
    }
  }, [storeRef])

  return (
    <>
      {calendarType === 'daily' &&
        closedDates.some((closedDate) => isSameDay(closedDate, currentDate)) &&
        (storeCalendars.some((storeCalendar) => {
          return storeCalendar.status === 'close' && storeCalendar.date === dateToString(currentDate)
        }) ? (
          <CautionFixedLabel label={'臨時休業日です'} />
        ) : (
          <CautionFixedLabel label={'休業日です'} />
        ))}
      <TopTabs />
      <div className={styles.standard}>
        <Spacer size={28} />
        <MainHeadline dataCy={'deliver-at-store'} headline={'お店でお渡し'} />
        <Spacer size={20} />
        <Description description={'任意の日程からお客さまの注文内容を確認できます。'} />
        <Spacer size={20} />
        <Calendar calendarType={calendarType} toggleCalendar={toggleCalendar} />
        <Spacer size={48} />
        <MainHeadline
          headline={'sacri上の営業時間・定休日'}
          iconPath={'/icon/blue/help.svg'}
          onClick={openBusinessHourHelp}
        />
        <Spacer size={20} />
        <BusinessHourCard />
        <Spacer size={48} />
        {roleManager.hasEnoughRole(9) && (
          <>
            <LargeMenuCard
              description={'アプリで取り扱う商品の登録編集や、アプリ上での表示並び替えができます。'}
              iconPath={'/icon/blue/bread.svg'}
              onClick={() => {
                dispatch(selectDefaultBreadTypes(['daily', 'preorder']))
                dispatch(push('/product'))
              }}
              url={'/product'}
              title={'出品する商品管理'}
            />
            <Spacer size={20} />
            <LargeMenuCard
              description={
                '当日取り置きできる商品の在庫を増減したり、お客さまに商品が取り置きできる通知を送ることが出来ます。'
              }
              iconPath={'/icon/blue/stock.svg'}
              url={'/stock'}
              title={'在庫の増減と通知'}
            />
            <Spacer size={20} />
            <LargeMenuCard
              description={'商品別に事前予約を受け付ける日を設定することができます。'}
              iconPath={'/icon/blue/reservation.svg'}
              subMenus={[
                {
                  description: '事前予約を受け付ける日をまとめて設定することができます。',
                  iconPath: '/icon/blue/collective_schedule.svg',
                  url: '/schedule/collective_setting',
                  title: '事前予約日一括設定',
                },
              ]}
              url={'/schedule'}
              title={'事前予約日設定'}
            />
            <Spacer size={20} />
            <LargeMenuCard
              description={'お客様の来店時や、商品購入に応じてポイントを付与し再来店を促すことができます。'}
              iconPath={'/icon/blue/point_card.svg'}
              subMenus={[
                {
                  description: 'ポイントを付与するQRコードを表示できます。',
                  iconPath: '/icon/blue/point_card_code.svg',
                  url: '/point_card/code',
                  title: 'QRコード表示',
                },
              ]}
              url={pointCard ? '/point_card' : '/point_card/create'}
              title={'ポイントカード'}
            />
            <Spacer size={20} />
          </>
        )}
        {roleManager.hasEnoughRole(5) && (
          <LargeMenuCard
            description={'お店の売上や簡易レポートを確認することができます。'}
            iconPath={'/icon/blue/revenue.svg'}
            url={'/account/revenue'}
            title={'売上確認'}
          />
        )}
        <Spacer size={72} />
      </div>
    </>
  )
}

export default TopTemplate
